import { useMemo } from "react";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import type { AgendaSettings, AppointmentStatus } from "@bookdental/schemas";
import { APPOINTMENT_STATUS_COLORS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { isOrderOverdue } from "@/lib/prosthesis-urgency";

type Props = {
  anchorDate: Date;
  settings: AgendaSettings;
  /** Compromissos de OUTRAS organizações (Agenda Pessoal) — mesclados no mesmo grid, sempre
   *  só-leitura (ver `organization` em cada item). */
  extraAppointments?: any[];
  /** Se false, omite os compromissos da própria organização ativa. Default true. */
  includeOwn?: boolean;
  onAppointmentEdit?: (id: string) => void;
  onDayClick?: (date: Date) => void;
  /** Clique num prazo de pedido de prótese (dia inteiro, sem hora). */
  onOrderClick?: (id: string) => void;
};

const WEEK_DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/**
 * Visão de mês estilo Google/Teams Calendar — grid 7×N com todos os
 * agendamentos como chips compactos.
 */
export function MonthView({
  anchorDate,
  settings: _settings,
  extraAppointments,
  includeOwn = true,
  onAppointmentEdit,
  onDayClick,
  onOrderClick,
}: Props) {
  const monthStart = startOfMonth(anchorDate);
  const monthEnd = endOfMonth(anchorDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = useMemo(() => {
    const arr: Date[] = [];
    let cur = gridStart;
    while (cur <= gridEnd) {
      arr.push(new Date(cur));
      cur = addDays(cur, 1);
    }
    return arr;
  }, [gridStart, gridEnd]);

  const numRows = days.length / 7;

  const apptsQuery = trpc.appointment.listByRange.useQuery(
    { rangeStart: gridStart, rangeEnd: gridEnd },
    { enabled: includeOwn },
  );
  const deadlineOrdersQuery = trpc.prosthesis.listByDeadlineRange.useQuery({ rangeStart: gridStart, rangeEnd: gridEnd });

  const deadlinesByDay = useMemo(() => {
    const map = new Map<string, Array<{ id: string; patientName: string; overdue: boolean }>>();
    for (const o of (deadlineOrdersQuery.data ?? []) as any[]) {
      if (!o.deadline) continue;
      const k = new Date(o.deadline).toDateString();
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push({ id: o.id, patientName: o.patientName, overdue: isOrderOverdue(o.deadline, o.status) });
    }
    return map;
  }, [deadlineOrdersQuery.data]);

  const byDay = useMemo(() => {
    const map = new Map<string, any[]>();
    // `enabled: false` não esvazia o cache da query de mesma chave que a página mantém ligada —
    // é aqui que includeOwn de fato tira as consultas próprias da grade (escopo "Organizações").
    const combined = [
      ...(includeOwn ? ((apptsQuery.data ?? []) as any[]) : []),
      ...(extraAppointments ?? []),
    ];
    for (const a of combined) {
      const k = new Date(a.date).toDateString();
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(a);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    return map;
  }, [apptsQuery.data, includeOwn, extraAppointments]);

  const today = new Date();

  return (
    <div className="flex h-full flex-col overflow-hidden border-t border-zinc-200 bg-white">
      {/* Header dos dias da semana */}
      <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50/60">
        {WEEK_DAYS.map((d) => (
          <div
            key={d}
            className="flex h-10 items-center justify-center text-[11px] font-semibold uppercase tracking-wider text-zinc-500"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grid de dias */}
      <div
        className="grid flex-1 grid-cols-7 overflow-auto"
        style={{ gridTemplateRows: `repeat(${numRows}, minmax(110px, 1fr))` }}
      >
        {days.map((date) => {
          const isCurrentMonth = isSameMonth(date, anchorDate);
          const isToday = isSameDay(date, today);
          const dayAppts = byDay.get(date.toDateString()) ?? [];
          const dayDeadlines = deadlinesByDay.get(date.toDateString()) ?? [];

          return (
            <MonthDayCell
              key={date.toISOString()}
              date={date}
              isCurrentMonth={isCurrentMonth}
              isToday={isToday}
              appointments={dayAppts}
              deadlineOrders={dayDeadlines}
              onDayClick={onDayClick}
              onAppointmentClick={onAppointmentEdit}
              onOrderClick={onOrderClick}
            />
          );
        })}
      </div>
    </div>
  );
}

type CellProps = {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  appointments: any[];
  deadlineOrders?: Array<{ id: string; patientName: string; overdue: boolean }>;
  onDayClick?: (date: Date) => void;
  onAppointmentClick?: (id: string) => void;
  onOrderClick?: (id: string) => void;
};

function MonthDayCell({
  date,
  isCurrentMonth,
  isToday,
  appointments,
  deadlineOrders = [],
  onDayClick,
  onAppointmentClick,
  onOrderClick,
}: CellProps) {
  const MAX_VISIBLE = 3;
  const visibleAppts = appointments.slice(0, MAX_VISIBLE);
  const overflow = appointments.length - MAX_VISIBLE;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("[data-appointment='1']")) return;
        onDayClick?.(date);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") onDayClick?.(date);
      }}
      className={`group relative flex cursor-pointer flex-col gap-1 border-b border-r border-zinc-100 p-1.5 transition-colors hover:bg-brand-50/30 ${
        !isCurrentMonth ? "bg-zinc-50/40" : ""
      }`}
    >
      {/* Número do dia */}
      <div className="flex items-center justify-end">
        <span
          className={`flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-semibold tabular-nums ${
            isToday
              ? "bg-brand-600 text-white"
              : isCurrentMonth
                ? "text-zinc-900"
                : "text-zinc-400"
          }`}
        >
          {format(date, "d")}
        </span>
      </div>

      {/* Appointments compactos */}
      <div className="flex-1 space-y-0.5 overflow-hidden">
        {deadlineOrders.slice(0, 2).map((o) => (
          <button
            key={o.id}
            type="button"
            data-appointment="1"
            onClick={(e) => {
              e.stopPropagation();
              onOrderClick?.(o.id);
            }}
            title={`Prazo do pedido de ${o.patientName}`}
            className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] font-semibold leading-tight ${
              o.overdue
                ? "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-500/15 dark:text-red-400"
                : "bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-400"
            }`}
          >
            {o.patientName}
          </button>
        ))}
        {visibleAppts.map((a) => {
          const color =
            a.color ?? APPOINTMENT_STATUS_COLORS[a.status as AppointmentStatus] ?? "#2457ff";
          const isCancelled = a.status === "CANCELLED";
          const isForeign = !!a.organization;
          return (
            <button
              key={a.id}
              type="button"
              data-appointment="1"
              onClick={(e) => {
                e.stopPropagation();
                onAppointmentClick?.(a.id);
              }}
              title={isForeign ? `${a.startTime} - ${a.patientName} (${a.organization.name})` : `${a.startTime} - ${a.patientName}`}
              className={`block w-full truncate rounded-full px-1.5 py-0.5 text-left text-[10px] font-semibold leading-tight transition-opacity hover:opacity-80 ${
                isCancelled ? "opacity-50 line-through" : ""
              } ${isForeign ? "opacity-80 ring-1 ring-dashed ring-zinc-400/50" : ""}`}
              style={{ backgroundColor: `${color}1a`, color }}
            >
              {a.startTime} - {a.patientName}
            </button>
          );
        })}
        {overflow > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDayClick?.(date);
            }}
            className="w-full rounded px-1 text-left text-[10px] font-medium text-brand-700 hover:bg-brand-50 hover:underline"
          >
            + {overflow} {overflow === 1 ? "outro" : "outros"}
          </button>
        )}
      </div>
    </div>
  );
}
